import { useState, useEffect } from "react";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

const ICON_OPTIONS = [
  "Hammer", "Bath", "Toilet", "Layers", "Grid2x2", "Ruler", "Paintbrush",
  "PaintRoller", "Square", "DoorOpen", "Home", "ThermometerSnowflake", "Wrench",
  "Pickaxe", "Sparkles", "Drill", "HardHat", "Brush", "Lightbulb", "Plug",
  "Fan", "TreePine", "Sun", "Building2", "Sofa", "Bed", "Key", "Shield",
  "Droplet", "Flame", "Zap", "Wind",
];

const empty = { icon: "Wrench", title: "", text: "", order: 0 };

export const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/services");
      setServices(res.data);
    } catch (e) {
      toast.error("Kon diensten niet laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, order: services.length }); setEditing("new"); };
  const openEdit = (s) => { setForm(s); setEditing(s.id); };
  const close = () => { setEditing(null); setForm(empty); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Titel is verplicht"); return; }
    setSaving(true);
    try {
      if (editing === "new") { await api.post("/admin/services", form); toast.success("Dienst toegevoegd"); }
      else { await api.put(`/admin/services/${editing}`, form); toast.success("Dienst bijgewerkt"); }
      close(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deze dienst verwijderen?")) return;
    await api.delete(`/admin/services/${id}`);
    toast.success("Dienst verwijderd");
    load();
  };

  if (loading) return <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Laden...</div>;

  return (
    <div data-testid="services-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold text-white">Diensten ({services.length})</h2>
        <button onClick={openNew} data-testid="add-service-button" className="bg-[#FF1A1A] text-white px-5 py-2.5 font-semibold flex items-center gap-2 hover:bg-[#E60000] transition-colors">
          <Plus size={18} /> Nieuwe dienst
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => {
          const Icon = Icons[s.icon] || Icons.Wrench;
          return (
            <div key={s.id} data-testid={`admin-service-${s.id}`} className="bg-[#111] border border-white/10 p-5">
              <Icon className="text-[#FF1A1A]" size={30} strokeWidth={1.4} />
              <h3 className="font-heading text-white font-semibold mt-3">{s.title}</h3>
              <p className="text-white/40 text-xs mt-1 line-clamp-2">{s.text}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`} className="text-white/60 hover:text-white flex items-center gap-1 text-xs border border-white/15 px-3 py-1.5"><Pencil size={13} /> Bewerk</button>
                <button onClick={() => remove(s.id)} data-testid={`delete-service-${s.id}`} className="text-white/60 hover:text-[#FF1A1A] flex items-center gap-1 text-xs border border-white/15 px-3 py-1.5"><Trash2 size={13} /> Verwijder</button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-10 px-4">
          <form onSubmit={save} data-testid="service-form" className="bg-[#111] border border-white/10 w-full max-w-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold text-white">{editing === "new" ? "Nieuwe dienst" : "Dienst bewerken"}</h3>
              <button type="button" onClick={close}><X className="text-white/50 hover:text-white" /></button>
            </div>
            <div className="flex flex-col gap-5">
              <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Titel (bv. Badkamers)" data-testid="service-title-input" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-2.5 text-white placeholder:text-white/30" />
              <textarea value={form.text} onChange={(e) => set("text", e.target.value)} placeholder="Korte omschrijving" rows={3} data-testid="service-text-input" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-2.5 text-white placeholder:text-white/30 resize-none" />
              <div>
                <label className="text-white/60 text-xs uppercase tracking-widest block mb-3">Kies een icoon</label>
                <div className="grid grid-cols-8 gap-2 max-h-44 overflow-y-auto p-1">
                  {ICON_OPTIONS.map((name) => {
                    const Ic = Icons[name] || Icons.Wrench;
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => set("icon", name)}
                        data-testid={`icon-option-${name}`}
                        title={name}
                        className={`aspect-square flex items-center justify-center border transition-colors ${form.icon === name ? "border-[#FF1A1A] bg-[#FF1A1A]/10 text-[#FF1A1A]" : "border-white/10 text-white/60 hover:border-white/30"}`}
                      >
                        <Ic size={20} strokeWidth={1.5} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <button type="submit" disabled={saving} data-testid="save-service-button" className="bg-[#FF1A1A] text-white px-8 py-3.5 font-semibold hover:bg-[#E60000] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {saving ? <><Loader2 size={18} className="animate-spin" /> Opslaan...</> : "Opslaan"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ServicesManager;
